/* eslint-disable react-hooks/exhaustive-deps */
import React, {useMemo} from "react"
import {last} from "lodash"
import TimezoneSelect, {i18nTimezones} from "react-timezone-select"
import momentTz from "moment-timezone"
import {dropdownStyle} from "./dropdownStyle"

const timezoneCityLabel = (timezone: string): string => last(timezone.split("/"))?.replace(/_/g, " ") || timezone

/** Match react-timezone-select's "(GMT±H:MM) Label" format. */
const formatTimezoneDisplayLabel = (timezone: string, cityLabel: string): string => {
    const offsetMinutes = momentTz.tz(timezone).utcOffset()
    const hr = `${(offsetMinutes / 60) ^ 0}:` + (offsetMinutes % 60 === 0 ? "00" : Math.abs(offsetMinutes % 60))
    return `(GMT${hr.includes("-") ? hr : `+${hr}`}) ${cityLabel}`
}

const getTimezoneValue = (value: unknown): string => {
    if (!value) {
        return ""
    }
    if (typeof value === "string") {
        return value
    }
    if (typeof value === "object" && value !== null && "value" in value) {
        return String((value as {value?: string}).value || "")
    }
    return ""
}

export function TimezoneDropdown(props) {
    const {stylesCustom, value, ...rest} = props
    let styles = dropdownStyle
    if (stylesCustom) {
        styles = {...styles, ...stylesCustom}
    }

    const guessedTimezone = momentTz.tz.guess()
    const timezoneValue = getTimezoneValue(value)

    // Keep valid IANA names (including aliases like Asia/Saigon) instead of remapping by
    // current UTC offset, which can change DST rules and silently rewrite stored values.
    // react-timezone-select only memoizes options on labelStyle, so unlisted zones must be
    // present in `timezones` on mount and the controlled value must include an explicit label.
    const timezones = useMemo(() => {
        const extras: Record<string, string> = {}
        if (guessedTimezone && momentTz.tz.zone(guessedTimezone)) {
            extras[guessedTimezone] = timezoneCityLabel(guessedTimezone)
        }
        if (timezoneValue && !(timezoneValue in i18nTimezones) && momentTz.tz.zone(timezoneValue)) {
            extras[timezoneValue] = timezoneCityLabel(timezoneValue)
        }
        return {...i18nTimezones, ...extras}
    }, [timezoneValue, guessedTimezone])

    const selectValue = useMemo(() => {
        if (!timezoneValue) {
            return ""
        }
        const cityLabel = timezones[timezoneValue] || timezoneCityLabel(timezoneValue)
        if (!momentTz.tz.zone(timezoneValue)) {
            return typeof value === "object" && value !== null ? value : timezoneValue
        }
        return {
            value: timezoneValue,
            label: formatTimezoneDisplayLabel(timezoneValue, cityLabel)
        }
    }, [timezoneValue, timezones, value])

    // Remount when the value is an unlisted IANA zone so the library rebuilds options
    // (its useMemo omits `timezones` from the dependency list).
    const selectKey = timezoneValue && !(timezoneValue in i18nTimezones) ? timezoneValue : "listed"

    return <TimezoneSelect key={selectKey} timezones={timezones} value={selectValue} styles={styles} {...rest} />
}
